/**
 * Map BillingError codes returned by staff-api to user-facing
 * translation keys under invoices.error.*. Falls back to a generic
 * message so unknown codes never crash the UI.
 */

import { ApiError } from './api';

type TFunction = (key: string, options?: Record<string, unknown>) => string;

const CODE_TO_KEY: Record<string, string> = {
  cancelled: 'error.cancelled',
  disputed: 'error.disputed',
  invoicing_hold: 'error.invoicingHold',
  not_invoiceable_status: 'error.notInvoiceableStatus',
  final_inspection_required: 'error.finalInspectionRequired',
  invoice_already_exists: 'error.invoiceAlreadyExists',
  invoice_not_found: 'error.invoiceNotFound',
  work_order_not_found: 'error.workOrderNotFound',
  invalid_invoice_state: 'error.invalidInvoiceState',
  overpayment: 'error.overpayment',
  receipt_settings_incomplete: 'error.receiptSettingsIncomplete',
  idempotency_key_conflict: 'error.idempotencyConflict',
  stale_invoice_version: 'error.staleVersion',
  invoice_not_finalizable: 'error.notFinalizable',
  non_positive_invoice: 'error.nonPositive',
  over_credit: 'error.overCredit',
  over_refund: 'error.overRefund',
  empty_export: 'error.emptyExport',
  export_batch_not_found: 'error.exportBatchNotFound',
  export_batch_not_cancellable: 'error.exportBatchNotCancellable',
  invoice_already_delivered: 'error.invoiceAlreadyDelivered',
  invoice_finalize_failed: 'error.invoiceFinalizeFailed',
  invoice_delivery_failed: 'error.invoiceDeliveryFailed',
  pdf_invalid: 'error.pdfInvalid',
  // #1323 — finalizing with the e-invoice channel until #1322 builds it. The
  // portal offers no channel picker today, so this cannot surface yet; mapped
  // so it is already localized whenever one is added.
  channel_not_implemented: 'error.channelNotImplemented',
  // #1321 interim — hand-typed terminal reference guards.
  invalid_card_reference: 'error.invalidCardReference',
  duplicate_card_reference: 'error.duplicateCardReference',
  payment_required: 'error.paymentRequired',
  svea_payments_not_ready: 'error.sveaPaymentsNotReady',
  svea_payment_zero_balance: 'error.sveaPaymentZeroBalance',
  svea_buyer_incomplete: 'error.sveaBuyerIncomplete',
  svea_refund_rejected: 'error.sveaRefundRejected',
  svea_mixed_refund: 'error.sveaMixedRefund',
  svea_refund_query_failed: 'error.sveaRefundQueryFailed',
  svea_void_cancel_pending: 'error.sveaVoidCancelPending',
  svea_void_cancel_rejected: 'error.sveaVoidCancelRejected',
  // #3925 — free text outside the customer's 24 h WhatsApp window is never delivered.
  svea_whatsapp_window_closed: 'error.sveaWhatsAppWindowClosed',
  // #3499 / #3500 — card terminal checkouts.
  card_terminal_not_enabled: 'error.terminal.notEnabled',
  terminal_checkout_not_found: 'error.terminal.checkoutNotFound',
  terminal_payment_not_found: 'error.terminal.paymentNotFound',
  terminal_checkout_closed: 'error.terminal.checkoutClosed',
  invalid_cash_amount: 'error.terminal.invalidCash',
  cash_covers_invoice: 'error.terminal.cashCoversInvoice',
  terminal_checkout_open: 'error.terminal.checkoutOpen',
  terminal_reader_not_found: 'error.terminal.readerNotFound',
  terminal_busy: 'error.terminal.busy',
  terminal_start_failed: 'error.terminal.startFailed',
  terminal_payments_disabled_in_test: 'error.terminal.disabledInTest',
  invalid_amount: 'error.terminal.invalidAmount',
  terminal_payment_in_progress: 'error.terminal.inProgress',
  amount_exceeds_remaining: 'error.terminal.amountExceedsRemaining',
  too_many_tenders: 'error.terminal.tooManyTenders',
  terminal_payment_not_pending: 'error.terminal.notPending',
  terminal_payment_not_unknown: 'error.terminal.notUnknown',
  terminal_payment_still_pending: 'error.terminal.stillPending',
  terminal_provider_unavailable: 'error.terminal.providerUnavailable',
  invoice_changed: 'error.terminal.invoiceChanged',
};

/**
 * Server `missingFields` values for receipt_settings_incomplete (#2019) and
 * invoice_not_finalizable (#2461). `contactEmail/contactPhone` is one combined
 * token, not two fields. Unknown keys stay as-is so a new server requirement
 * still renders.
 *
 * Both error codes share {@link formatMissingFieldLabels}, but only the
 * receipt tokens were ever mapped — the four finalization tokens from
 * finalize-draft-invoice.ts rendered as raw identifiers ("sellerIban") in the
 * user's face.
 */
const MISSING_FIELD_LABEL_KEYS: Record<string, string> = {
  businessId: 'error.missingField.businessId',
  address: 'error.missingField.address',
  'contactEmail/contactPhone': 'error.missingField.contactEmailOrPhone',
  buyerEmail: 'error.missingField.recipientEmail',
  buyerName: 'error.missingField.recipientName',
  paymentReferenceNumber: 'error.missingField.paymentReferenceNumber',
  sellerIban: 'error.missingField.sellerIban',
  recipientName: 'error.missingField.recipientName',
  recipientEmail: 'error.missingField.recipientEmail',
  recipientEInvoiceAddress: 'error.missingField.recipientEInvoiceAddress',
  positiveTotal: 'error.missingField.positiveTotal',
};

interface ErrorBody {
  code?: unknown;
  invoiceId?: unknown;
  invoiceNumber?: unknown;
  step?: unknown;
  message?: unknown;
  remainingCents?: unknown;
  currentStatus?: unknown;
  missingFields?: unknown;
  sveaRefundableCents?: unknown;
  nonSveaRefundableCents?: unknown;
}

function formatCentsEuro(cents: number): string {
  return (cents / 100).toFixed(2).replace('.', ',');
}

function readBody(err: unknown): ErrorBody | null {
  if (err instanceof ApiError && err.data && typeof err.data === 'object') {
    return err.data as ErrorBody;
  }
  return null;
}

export function formatMissingFieldLabels(raw: unknown, t: TFunction): string {
  if (!Array.isArray(raw)) {
    return '';
  }
  return raw
    .filter((item): item is string => typeof item === 'string' && item.length > 0)
    .map((item) => {
      const labelKey = MISSING_FIELD_LABEL_KEYS[item];
      return labelKey !== undefined ? t(labelKey) : item;
    })
    .join(', ');
}

/** True when PATCH /invoices/:id lost the optimistic lock (#2150). */
/**
 * The invoice was created but a later issue step failed (#3112). The caller
 * should close its dialog and let the Maksut card show the invoice as it is.
 */
export function invoiceIssueStepFailure(err: unknown): { invoiceId: string; step: string } | null {
  const body = readBody(err);
  if (!body) return null;
  if (body.code !== 'invoice_finalize_failed' && body.code !== 'invoice_delivery_failed') return null;
  return typeof body.invoiceId === 'string'
    ? { invoiceId: body.invoiceId, step: typeof body.step === 'string' ? body.step : '' }
    : null;
}

/** The stable `code` of a billing error response, if there is one. */
export function billingErrorCode(err: unknown): string | undefined {
  const body = readBody(err);
  return body && typeof body.code === 'string' ? body.code : undefined;
}

export function isStaleInvoiceVersionError(err: unknown): boolean {
  const body = readBody(err);
  return body !== null && body.code === 'stale_invoice_version';
}

export function translateBillingError(err: unknown, t: TFunction): string {
  const body = readBody(err);
  const code = body && typeof body.code === 'string' ? body.code : undefined;
  const key = code ? CODE_TO_KEY[code] : undefined;
  if (key) {
    const interp: Record<string, unknown> = {};
    if (typeof body?.remainingCents === 'number') {
      interp['remaining'] = formatCentsEuro(body.remainingCents);
    }
    if (typeof body?.sveaRefundableCents === 'number') {
      interp['svea'] = formatCentsEuro(body.sveaRefundableCents);
    }
    if (typeof body?.nonSveaRefundableCents === 'number') {
      interp['other'] = formatCentsEuro(body.nonSveaRefundableCents);
    }
    if (typeof body?.currentStatus === 'string') {
      interp['status'] = body.currentStatus;
    }
    if (typeof body?.invoiceNumber === 'string') {
      interp['invoiceNumber'] = body.invoiceNumber;
    }
    if (typeof body?.message === 'string') {
      interp['message'] = body.message;
    }
    const fields = formatMissingFieldLabels(body?.missingFields, t);
    if (fields !== '') {
      interp['fields'] = fields;
    }
    // Empty {{fields}} would leave a broken sentence ("puuttuu .").
    if (key === 'error.receiptSettingsIncomplete' && fields === '') {
      return t('error.receiptSettingsIncompleteNoFields');
    }
    return t(key, interp);
  }

  // Fallback: server-provided message, then generic
  if (body && typeof body.message === 'string' && body.message.length > 0) {
    return body.message;
  }
  return t('error.generic');
}
